import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { Clock } from "lucide-react";
import { steps } from "@/data/onboarding.data";
import { useOnboarding } from "@/context/OnboardingContext";
import useContentGenerationAndWorkflow from "./useContentGenerationAndWorkflow";
import useUserInteractionHandling from "./useUserInteractionHandling";
import config from "@/config";

export default function useOnboardingSetupAndInitialization() {
  const { state, setState, setStepTexts, setupStarted, setExpandedSteps } = useOnboarding();
  const { handleCallEdgeFunction, handleIdeasGenerated } = useContentGenerationAndWorkflow();
  const { handleUpdateUrlParams } = useUserInteractionHandling();

  // Set default language to English
  const hanldeSetDefaultLanguage = () => {
    const websiteInfo = JSON.parse(localStorage.getItem("website_info") || "{}");
    websiteInfo.language = "en";
    localStorage.setItem("website_info", JSON.stringify(websiteInfo));

    // Also store directly as website_language for consistent access
    localStorage.setItem("website_language", "en");

    console.log("Using default language: en");
  };

  const handleStartSetup1 = async () => {
    console.log("Starting Setup 1 (Chat version)...");
    
    // Mark setup as started to prevent multiple initializations
    setupStarted.current = true;

    // Reset state
    setState((prevState) => ({
      ...prevState,
      progress: 0,
      currentStepIndex: 0,
      currentStepText: "Setting up your workspace...",
    }));

    // Reset step texts
    setStepTexts({});

    // Create website and organization IDs with uuid
    const websiteId = localStorage.getItem("website_id") || uuidv4();
    const organisationId = localStorage.getItem("organisation_id") || uuidv4();

    // Store IDs in localStorage
    localStorage.setItem("website_id", websiteId);
    localStorage.setItem("organisation_id", organisationId);
    localStorage.setItem("current_organisation_id", organisationId); // Add this line to store current org ID

    // Create organisation name from URL
    const urlWithoutProtocol = state.websiteUrl.replace(/^https?:\/\//, "");
    const domainParts = urlWithoutProtocol.split(".");
    const organisationName =
      domainParts.length > 1 ? domainParts[domainParts.length - 2] : urlWithoutProtocol;

    // Store website info
    const websiteInfo = {
      id: websiteId,
      url: state.websiteUrl,
      name: organisationName,
      organisation_id: organisationId,
      created_at: new Date().toISOString(),
    };
    localStorage.setItem("website_info", JSON.stringify(websiteInfo));

    // Store organisation info
    const organisationInfo = {
      id: organisationId,
      name: organisationName.charAt(0).toUpperCase() + organisationName.slice(1),
      created_at: new Date().toISOString(),
    };
    localStorage.setItem("organisation_info", JSON.stringify(organisationInfo));
    localStorage.setItem("currentOrganisation", JSON.stringify(organisationInfo));

    // Ensure onboarding analytics logs a 'started' event for WP entry path as well
    try {
      await handleCallEdgeFunction("store-onboarding-analytics", {
        website_id: websiteId,
        website_url: state.websiteUrl,
        status: "started",
        previous_website_id: localStorage.getItem("previous_website_id") || undefined,
      });
    } catch (e) {
      // Non-blocking: continue onboarding even if analytics logging fails
      console.warn("Failed to log onboarding started status:", e);
    }

    // Also store the full organisation object for the OrganisationContext
    console.log("Storing full organisation data in localStorage for OrganisationContext");
    localStorage.setItem("currentOrganisation", JSON.stringify(organisationInfo));

    // Setup 1 steps - these will all run without authentication
    const setup1Steps = [
      // Step 1: Account Setup
      async () => {
        setState((prev) => ({
          ...prev,
          currentStepIndex: 0,
          currentStepText: "Creating workspace...",
        }));
        await new Promise((r) => setTimeout(r, 1000));
        return (
          "No account? No problem! We made a quick site and organisation for you.\n\n✅ You're now set up with a private workspace based on your website url: " +
          state.websiteUrl +
          ". Everything is local, just for you."
        );
      },

      // Step 2: Reading Website
      async () => {
        setState((prev) => ({
          ...prev,
          currentStepIndex: 1,
          currentStepText: "Reading website...",
        }));
        try {
          // Try get-sitemap-pages function
          console.log("Attempting to call get-sitemap-pages with website_id:", websiteId);

          // Try with snake_case parameters
          const payload = {
            website_id: websiteId,
            website_url: state.websiteUrl,
            custom_sitemap_url: null,
          };

          let result = null;

          try {
            console.log("Trying get-sitemap-pages with payload:", payload);
            result = await handleCallEdgeFunction("get-sitemap-pages", payload);
          } catch (error) {
            console.error("Failed with payload:", payload, error);
            throw error; // Let the error propagate up
          }

          if (result?.pages && result.pages.length > 0) {
            const limitedPages = result.pages.slice(0, 200);
            console.log(`Successfully loaded ${limitedPages.length} pages from sitemap`);
            console.log("Sample sitemap page structure:", limitedPages[0]);
            console.log(
              "Page URLs sample:",
              limitedPages.slice(0, 5).map((p) => p.url)
            );

            // Store pages in localStorage for later use
            localStorage.setItem("website_content", JSON.stringify(limitedPages));

            // Make sure other functions can access this data
            await new Promise((resolve) => setTimeout(resolve, 500));

            return `We've just read your website — exciting! 😄 We can always do some more reading later.\n\n🧠 ${limitedPages.length} pages have been loaded from your sitemap.`;
          } else {
            // If no pages from sitemap, try crawling
            try {
              // Try crawl-website-pages function with snake_case
              console.log("Trying crawl-website-pages with payload:", payload);
              const crawlResult = await handleCallEdgeFunction(
                "crawl-website-pages",
                payload
              );

              if (crawlResult?.pages && crawlResult.pages.length > 0) {
                const limitedPages = crawlResult.pages.slice(0, 200);
                console.log(
                  `Successfully loaded ${limitedPages.length} pages by crawling website`
                );

                // Store pages in localStorage for later use
                localStorage.setItem("website_content", JSON.stringify(limitedPages));

                // Make sure other functions can access this data
                await new Promise((resolve) => setTimeout(resolve, 500));

                return `We've just read your website — exciting! 😄 We can always do some more reading later.\n\n🧠 ${limitedPages.length} pages have been loaded by scanning your website.`;
              }
            } catch (error) {
              console.error("Failed crawl with payload:", payload, error);

              // Store the error in onboarding analytics
              try {
                await handleCallEdgeFunction("store-onboarding-analytics", {
                  website_id: websiteId,
                  website_url: state.websiteUrl,
                  status: "error",
                  error: error.message,
                  error_code: error.code || "CRAWL_ERROR",
                  error_step: "2",
                  error_details: error.details || error.message,
                  worker_response: error.response || null,
                });
              } catch (storeError) {
                console.error("Failed to store error in analytics:", storeError);
              }

              throw error; // Let the error propagate up
            }

            // If we got here, all attempts failed
            throw new Error(
              "Could not find any pages on the website. Please check the URL and try again."
            );
          }
        } catch (error) {
          console.error("Error reading website:", error);

          // Store the error in onboarding analytics
          try {
            await handleCallEdgeFunction("store-onboarding-analytics", {
              website_id: websiteId,
              website_url: state.websiteUrl,
              status: "error",
              error: `Error in step 2: ${error.message}`,
              error_code: error.code || "WEBSITE_READ_ERROR",
              error_step: "2",
              error_details: error.details || error.message,
              worker_response: error.response || null,
            });
          } catch (storeError) {
            console.error("Failed to store error in analytics:", storeError);
          }

          throw error; // Let the error propagate up
        }
      },

      // Step 3: Language Detection (hidden from user)
      async () => {
        setState((prev) => ({
          ...prev,
          currentStepIndex: 2,
          currentStepText: "Detecting language...",
        }));
        try {
          const result = await handleCallEdgeFunction("detect-website-language", {
            website_id: websiteId,
            website_url: state.websiteUrl,
          });

          if (result?.language) {
            // Store language in multiple locations for cross-compatibility
            const websiteInfo = JSON.parse(localStorage.getItem("website_info") || "{}");
            websiteInfo.language = result.language;
            localStorage.setItem("website_info", JSON.stringify(websiteInfo));

            // Also store directly as website_language for consistent access
            localStorage.setItem("website_language", result.language);

            console.log(`Language detected: ${result.language}`);

            // Log extra information for Danish sites
            if (result.language === "da") {
              console.log("Danish language detected - will generate Danish content");
            }

            // Dispatch event to notify chat components about language detection
            if (result.language !== 'en') {
              window.dispatchEvent(new CustomEvent('language-detected', { 
                detail: { language: result.language, websiteId, websiteUrl: state.websiteUrl } 
              }));
            }
          } else {
            // Set default language if detection fails
            hanldeSetDefaultLanguage();
          }
          return ""; // No message shown to user
        } catch (error) {
          console.error("Error detecting language:", error);
          // Set default language if detection fails
          hanldeSetDefaultLanguage();
          return "";
        }
      },

      // Step 4: Learning Tone-of-Voice
      async () => {
        setState((prev) => ({
          ...prev,
          currentStepIndex: 3,
          currentStepText:
            "Learning how you sound so we can write like you.\n🔍 Selecting and prioritising key pages to understand your tone.",
        }));
        try {
          // Get ALL sitemap pages stored in localStorage
          const rawWebsiteContent = JSON.parse(
            localStorage.getItem("website_content") || "[]"
          );

          console.log(
            `Found ${rawWebsiteContent.length} raw sitemap pages in localStorage`
          );

          if (rawWebsiteContent.length > 0) {
            console.log("Raw sitemap page sample:", rawWebsiteContent[0]);
            console.log(
              "First 5 page URLs:",
              rawWebsiteContent.slice(0, 5).map((p) => p.url)
            );
          }

          // Helper to extract title from URL
          const extractTitleFromUrl = (url: string): string => {
            try {
              const urlObj = new URL(url);
              const path = urlObj.pathname;

              // If it's the homepage
              if (path === "/" || path === "") {
                return "Homepage";
              }

              // Get the last path segment
              const segments = path.split("/").filter(Boolean);
              if (segments.length === 0) {
                return "Homepage";
              }

              let lastSegment = segments[segments.length - 1];

              // Remove file extensions
              lastSegment = lastSegment.replace(/\.(html|php|asp|aspx)$/, "");

              // Replace hyphens and underscores with spaces
              lastSegment = lastSegment.replace(/[-_]/g, " ");

              // Capitalize first letter of each word
              lastSegment = lastSegment
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              return lastSegment || "Page";
            } catch (error) {
              console.error(`Error extracting title from URL ${url}`, error);
              return "Page";
            }
          };

          // Format ALL the sitemap pages to match the expected WebsiteContent structure
          const formattedWebsiteContent = rawWebsiteContent.map(
            (page: any, index: number) => ({
              id: page.id || `page-${Date.now()}-${index}`,
              title: page.title || extractTitleFromUrl(page.url),
              url: page.url,
              content_type: page.content_type || "page",
              is_cornerstone: false,
            })
          );

          console.log(
            `Formatted ALL ${formattedWebsiteContent.length} sitemap pages for suggest-key-content`
          );

          if (formattedWebsiteContent.length === 0) {
            throw new Error(
              "No pages found in website content. Please try a different website."
            );
          }

          // Update the step text to show a more fluid message
          setState((prev) => ({
            ...prev,
            currentStepText:
              "Learning how you sound so we can write like you.\n🔍 Selecting and prioritising key pages to understand your tone and style.",
          }));

          // Send ALL pages to suggest-key-content so it has full context
          const payload = {
            website_id: websiteId,
            website_url: state.websiteUrl,
            sitemap_pages: formattedWebsiteContent,
            pages: formattedWebsiteContent,
            is_onboarding: true,
          };

          console.log(
            `Sending ${formattedWebsiteContent.length} pages to suggest-key-content`
          );

          const result = await handleCallEdgeFunction("suggest-key-content", payload);

          console.log("suggest-key-content result:", result);

          if (!result) {
            throw new Error("No response received from suggest-key-content");
          }

          if (result.error) {
            throw new Error(result.error.message || "Error from suggest-key-content");
          }

          if (result?.suggestions && result.suggestions.length > 0) {
            console.log(`Received ${result.suggestions.length} key content suggestions`);

            // Improve logging to show clearer information about the suggested pages
            const samplePages = result.suggestions.slice(0, 5);
            console.log(
              "Suggestions data sample:",
              samplePages.map((p) => ({
                title: p.title,
                path: p.url ? new URL(p.url).pathname : "/",
                id: p.id,
              }))
            );

            const selectedPages = result.suggestions.slice(0, 5);
            localStorage.setItem("key_content_pages", JSON.stringify(selectedPages));

            // Extract just the path portion for display
            const pageUrls = selectedPages.map((page: any) => {
              try {
                // Try to parse the URL and get just the pathname
                const url = page.url ? new URL(page.url).pathname : "/";
                const displayUrl = url === "/" ? "/ (Homepage)" : url;
                return displayUrl;
              } catch (e) {
                // Fallback if URL parsing fails
                return page.url?.replace(state.websiteUrl, "") || "/";
              }
            });

            // Add a small delay before showing the final message to make the transition smoother
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Return the message with the page URLs
            return `We've learned how you sound so we can write like you.\n🔍 Selected and prioritised these key pages to understand your tone and style:\n${pageUrls.join(
              "\n"
            )}\n(Don't worry, you can adjust this later.)`;
          } else {
            // Don't use mock data - show the actual error
            console.error("No suggestions returned from suggest-key-content:", result);
            throw new Error(
              "Could not identify key content pages. Please try a different website."
            );
          }
        } catch (error) {
          console.error("Error learning tone:", error);

          // Don't mask errors with mock data - show the real issue
          toast.error(
            "Error generating ideas: " +
              (error.message || "Failed to generate content ideas. Please try again.")
          );
          throw error;
        }
      },

      // Step 5: Reading Key Content
      async () => {
        setState((prev) => ({
          ...prev,
          currentStepIndex: 4,
          currentStepText:
            "We're digging deeper into your key content to fully understand your voice and topics.",
        }));
        try {
          const keyPages = JSON.parse(localStorage.getItem("key_content_pages") || "[]");
          let success = false;

          // Improved logging to show more useful information about key pages
          console.log(`Reading key content for ${keyPages.length} pages`);

          if (keyPages.length > 0) {
            // Log detailed key page information
            console.log(
              "Key pages detailed data:",
              keyPages.map((page) => ({
                id: page.id,
                title: page.title || "Untitled",
                url: page.url || "No URL",
                reason: page.reason?.substring(0, 50) + "...", // Truncate long reasons
              }))
            );

            // Create an empty array to store the scraped content
            const scrapedContent = [];

            try {
              // Process each key page individually
              for (const page of keyPages) {
                // Make sure we're using the full URL from the page data, not constructing it from ID
                const pageUrl = page.url;

                if (!pageUrl) {
                  console.error(`No valid URL found for page:`, page);
                  continue;
                }

                console.log(
                  `Scraping content for page: ${page.title || "Untitled"} (${pageUrl})`
                );

                // Update the step text to show a more fluid message for each page
                setState((prev) => ({
                  ...prev,
                  currentStepText: `We're digging deeper into your key content to fully understand your voice and topics.\n🔍 Analyzing: ${
                    page.title || "Page"
                  }\n${pageUrl}`,
                }));

                // Add a small delay to make the animation feel more natural
                await new Promise((resolve) => setTimeout(resolve, 800));

                // Call scrape-content to actually get the content
                const scrapeResult = await handleCallEdgeFunction("scrape-content", {
                  website_id: websiteId,
                  website_url: pageUrl,
                });

                // Log the full scrape result for debugging
                console.log(
                  `Full scrape result for ${pageUrl}:`,
                  JSON.stringify(scrapeResult)
                );

                // Log a summary of the scrape result rather than the full object
                if (scrapeResult?.pages && scrapeResult.pages.length > 0) {
                  console.log(
                    `Scrape successful for ${pageUrl}: received ${
                      scrapeResult.pages.length
                    } pages with a total of approximately ${scrapeResult.pages.reduce(
                      (sum, p) => sum + (p.content?.length || 0),
                      0
                    )} characters of content`
                  );

                  // Add the scraped content to our array with page association
                  scrapeResult.pages.forEach((scrapedPage) => {
                    scrapedContent.push({
                      ...scrapedPage,
                      page_id: page.id,
                      original_url: pageUrl,
                      title: page.title || scrapedPage.title || "Untitled Page",
                    });
                  });

                  success = true;
                } else {
                  console.error(`Scrape failed for ${pageUrl}:`, scrapeResult);

                  // Create a placeholder content entry even if scraping failed
                  // This ensures we have at least some reference to the key page
                  scrapedContent.push({
                    id: `placeholder-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                    page_id: page.id,
                    original_url: pageUrl,
                    title: page.title || "Untitled Page",
                    content: "",
                    digest: `This is a key page from ${pageUrl} but content could not be scraped.`,
                  });
                }
              }

              // Store the scraped content array in localStorage
              if (scrapedContent.length > 0) {
                localStorage.setItem("scraped_content", JSON.stringify(scrapedContent));
                console.log(
                  `Stored ${scrapedContent.length} scraped content entries in localStorage with detailed info:`,
                  scrapedContent.map((c) => ({
                    page_id: c.page_id,
                    title: c.title,
                    content_length: c.content?.length || 0,
                    digest_length: c.digest?.length || 0,
                  }))
                );
              } else {
                console.error("No scraped content was retrieved from any key pages");
              }
            } catch (scrapeError) {
              console.error("Error scraping content:", scrapeError);
              throw scrapeError; // Let the error propagate to show real issues
            }
          } else {
            console.error(
              "No key pages found to analyze - check suggest-key-content response"
            );
            throw new Error("No key pages found to analyze");
          }

          // Even if some pages failed, consider it a success if we have some content
          const scrapedContentAfter = JSON.parse(
            localStorage.getItem("scraped_content") || "[]"
          );
          if (scrapedContentAfter.length > 0) {
            success = true;
            console.log(
              `Successfully stored ${scrapedContentAfter.length} scraped content entries`
            );

            // Extract offering and goal from scraped content
            try {
              console.log("Extracting offering and goal from scraped content...");
              const website_id = localStorage.getItem("website_id");
              const website_url = localStorage.getItem("onboardingWebsite");

              if (website_id && website_url) {
                const extractResponse = await fetch(`${config.supabase.functionsUrl}/extract-offering-goal`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  },
                  body: JSON.stringify({
                    website_id,
                    website_url,
                    scraped_content: scrapedContentAfter
                  })
                });

                if (extractResponse.ok) {
                  const extractResult = await extractResponse.json();
                  if (extractResult.success) {
                    // Store offering and goal in localStorage
                    localStorage.setItem("website_offering", extractResult.offering || "");
                    localStorage.setItem("website_goal", extractResult.goal || "");
                    console.log("Successfully extracted and stored offering and goal:", {
                      offering: extractResult.offering,
                      goal: extractResult.goal
                    });
                  } else {
                    console.warn("Failed to extract offering and goal:", extractResult.error);
                  }
                } else {
                  console.warn("Failed to call extract-offering-goal function");
                }
              }
            } catch (extractError) {
              console.error("Error extracting offering and goal:", extractError);
              // Don't throw - this is not critical for the main flow
            }
          }

          if (!success) {
            throw new Error("Failed to scrape content from any key pages");
          }

          // Add a small delay before showing the final message to make the transition smoother
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Auto-fold the "Reading key content" step once completed
          setExpandedSteps((prev) => ({
            ...prev,
            5: false // Step 5 is "Reading key content"
          }));

          return "We've successfully analyzed your key content to understand your voice and topics.\n\nYour most important pages have been processed — this helps us learn how to write like you.";
        } catch (error) {
          console.error("Error reading content:", error);
          // Don't mask errors with mock data - show the real issue
          throw error;
        }
      },

    ];

    // Run all setup1 steps in sequence
    const totalSteps = setup1Steps.length;
    try {
      for (let i = 0; i < totalSteps; i++) {
        // Update progress
        setState((prev) => ({
          ...prev,
          progress: (i / totalSteps) * 100,
        }));

        try {
          // Run current step
          const message = await setup1Steps[i]();

          // Display message if any
          if (message) {
            setState((prev) => ({ ...prev, currentStepText: message }));

            // Store the message for this step
            setStepTexts((prev) => ({
              ...prev,
              [steps[i].id]: message,
            }));

            await new Promise((r) => setTimeout(r, 2000)); // Pause to show message
          }

          // After each step, verify that required data was saved to localStorage
          if (i === 1) {
            // After Reading Website step
            const websiteContent = JSON.parse(
              localStorage.getItem("website_content") || "[]"
            );
            if (websiteContent.length === 0) {
              console.error("No website content saved after step 2 - critical error");
              throw new Error(
                "Failed to load any website pages. Please check the URL or try a different website."
              );
            } else {
              console.log(
                `Successfully saved ${websiteContent.length} pages to localStorage after step 2`
              );
            }
          } else if (i === 3) {
            // After Learning Tone-of-Voice
            const keyContentPages = JSON.parse(
              localStorage.getItem("key_content_pages") || "[]"
            );
            if (keyContentPages.length === 0) {
              console.error("No key content pages saved after step 4 - critical error");
              throw new Error(
                "Failed to identify key pages. Please try again or try a different website."
              );
            } else {
              console.log(
                `Successfully identified ${keyContentPages.length} key pages after step 4`
              );
            }
          }
        } catch (stepError) {
          console.error(`Error in step ${i + 1}:`, stepError);
          // Show toast for visibility
          toast("Error", {
            description: `Error in step ${i + 1}: ${
              stepError.message
            }. Please refresh and try again.`,
          });
          setState((prev) => ({
            ...prev,
            currentStepText: `Error in step ${i + 1}: ${
              stepError.message
            }. Please refresh and try again.`,
          }));
          return; // Stop execution on error
        }
      }

      // Before completing, verify all required data is present
      const websiteContent = JSON.parse(localStorage.getItem("website_content") || "[]");
      const keyContentPages = JSON.parse(
        localStorage.getItem("key_content_pages") || "[]"
      );

      if (
        websiteContent.length === 0 ||
        keyContentPages.length === 0
      ) {
        console.error("Missing critical data before completing onboarding", {
          websiteContentCount: websiteContent.length,
          keyContentCount: keyContentPages.length,
        });

        toast("Error Completing Setup", {
          description:
            "Missing required data to complete the setup. Please refresh and try again.",
        });

        setState((prev) => ({
          ...prev,
          currentStepText: `Error: Missing required data to complete setup. Please refresh and try again.`,
        }));
        return;
      }

      // Complete Setup 1
      setState((prev) => ({
        ...prev,
        progress: 100,
        currentStepIndex: 4, // Mark all 5 steps as completed (0-indexed, so 4 = 5th step)
        step: 2, // Move to Setup 2: Content Idea Selection
      }));

      // Don't update URL to preserve chat state
    } catch (error) {
      console.error("Error in setup process:", error);
      toast("Setup Error", {
        description:
          error.message || "An unexpected error occurred. Please refresh and try again.",
      });
      setState((prev) => ({
        ...prev,
        currentStepText: `Onboarding error: ${error.message}. Please refresh and try again.`,
      }));
    }
  };

  // Get default posting days based on frequency
  const handleGetDefaultPostingDays = (frequency: number): string[] => {
    switch (frequency) {
      case 1:
        return ["monday"];
      case 2:
        return ["monday", "thursday"];
      case 3:
        return ["monday", "wednesday", "friday"];
      case 4:
        return ["monday", "tuesday", "thursday", "friday"];
      case 5:
        return ["monday", "tuesday", "wednesday", "thursday", "friday"];
      case 6:
        return ["monday", "monday", "tuesday", "wednesday", "thursday", "friday"];
      case 7:
        return [
          "monday",
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "thursday",
          "friday",
        ];
      case 8:
        return [
          "monday",
          "monday",
          "tuesday",
          "tuesday",
          "wednesday",
          "thursday",
          "thursday",
          "friday",
        ];
      case 9:
        return [
          "monday",
          "monday",
          "tuesday",
          "tuesday",
          "wednesday",
          "wednesday",
          "thursday",
          "thursday",
          "friday",
        ];
      case 10:
        return [
          "monday",
          "monday",
          "tuesday",
          "tuesday",
          "wednesday",
          "wednesday",
          "thursday",
          "thursday",
          "friday",
          "friday",
        ];
      default:
        return ["monday", "wednesday", "friday"];
    }
  };

  // Helper for length tag info (copied from TitleSuggestion)
  const handleGetLengthTagInfo = (tag: string) => {
    switch ((tag || "").toLowerCase()) {
      case "ultra-short":
        return { icon: <Clock className="h-3 w-3 mr-1" />, description: "0-300 words" };
      case "short":
        return { icon: <Clock className="h-3 w-3 mr-1" />, description: "300-600 words" };
      case "medium":
        return { icon: <Clock className="h-3 w-3 mr-1" />, description: "600-900 words" };
      case "medium-long":
        return {
          icon: <Clock className="h-3 w-3 mr-1" />,
          description: "900-1200 words",
        };
      case "long":
        return {
          icon: <Clock className="h-3 w-3 mr-1" />,
          description: "1200-1800 words",
        };
      case "very-long":
        return { icon: <Clock className="h-3 w-3 mr-1" />, description: "1800+ words" };
      default:
        return {
          icon: <Clock className="h-3 w-3 mr-1" />,
          description: "Standard length article",
        };
    }
  };

  // Handle frequency change
  const handleFrequencyChange = (frequency: number) => {
    setState((prev) => ({
      ...prev,
      postingFrequency: frequency,
      // Only auto-fill days when user hasn't made manual selections yet
      postingDays: prev.postingDays && prev.postingDays.length > 0
        ? prev.postingDays
        : handleGetDefaultPostingDays(frequency),
    }));
  };

  return {
    handleStartSetup1,
    hanldeSetDefaultLanguage,
    handleGetDefaultPostingDays,
    handleGetLengthTagInfo,
    handleFrequencyChange,
  };
}
